/**
 * Patinfo search/response DTOs
 * Mirror the C# PatinfoSearch* and PatinfoProfile* DTOs used by provider_profile_search
 * and provider_profile_put service requests.
 */

import type { AdminPreLoadDto, AdminUVarDto, SettingDto } from './service';
import type { AdminResponseDto } from '../AdminResponseDto';

// ── Request ──────────────────────────────────────────────────────────────────

export type PatinfoSearchType =
  | 'profiles'
  | 'recentreports'
  | 'openadmins'
  | 'flags'
  | 'messages';

export type PatinfoSearchField = 'patid' | 'fname' | 'lname' | 'dob';

export type PatinfoSearchInclude =
  | 'launch'
  | 'history'
  | 'schedule'
  | 'connections'
  | 'flags'
  | 'extended'
  | 'episodes'
  | 'messages';

export type PatinfoSearchScope = 'local' | 'practice' | 'profile';

export interface PatinfoSearchTermDto {
  searchField: PatinfoSearchField;
  searchTerm: string;
}

export interface PatinfoSearchFilterDto {
  searchField: string;
  searchFilter: string;
}

export interface PatinfoSearchRequestDto {
  type: PatinfoSearchType;
  blocknum: string;
  blocksize: string;
  searchIncludes: PatinfoSearchInclude[];
  scope: PatinfoSearchScope;
  searchTerms: PatinfoSearchTermDto[];
  searchFilters: PatinfoSearchFilterDto[];
}

// ── Response ─────────────────────────────────────────────────────────────────

export interface PatinfoSearchResultDto {
  patcode: string;
  patientid: string;
  /** Which AdminResponseDto (PatinfoHistoryDto.Admin.RecId) this result points to — set for 'recentreports' searches. */
  recid: string;
}

export interface PatinfoPhiDto {
  name: string;
  value: string;
}

/**
 * A single notification event (past, pending, or forced) attached to a launch.
 * Status: '0' = pending, '-1' = skipped, '1' = sent, '2' = force-send immediately
 * (used by "Send Now" to bypass scheduling).
 */
export interface LaunchNotifyEventDto {
  Type: string;
  EDate: string; // event datetime
  Sent: string; // notify sent datetime
  Status: string;
}

/**
 * A secure assessment launch attached to a client profile (provider_profile_search
 * launches / launch_secure_put). SSKey is blank on insert (Status >= '2') — the
 * backend assigns it and returns the full LaunchSecureDto in the response, which
 * should be appended to PatinfoProfileDto.launches locally.
 */
export interface LaunchSecureDto {
  SSKey: string;
  AssessmentId: string;
  DName: string;
  /** Origin (scheme + host) the /secure/{SSKey} link should be built against — the backend
   * has no other way to know which frontend origin issued this launch. */
  BaseURL: string;
  Phi: PatinfoPhiDto[];
  /** Snapshot of AccountPlaDto.UVars at launch-creation time — see ClientLaunchBuilder.ts. */
  UVars: AdminUVarDto[];
  Preloads: AdminPreLoadDto[];
  Extra: SettingDto[];
  NotifyState: LaunchNotifyEventDto[] | null;
  DueDate: string; // includes time of day offset
  Terminate: string;
  Status: string;
}

/**
 * Wire (PascalCase) shape of the AdminResponseDto embedded in PatinfoHistoryDto.Admin —
 * ServicesController's flat-stringified DTOs never pass through ASP.NET's camelCase JSON
 * policy, so this stays PascalCase on the wire (same convention documented in
 * tokenAssessment.ts for a different DTO). This mirrors the subset of the C# AdminResponseDto
 * that actually exists server-side — see AdminResponseDto.ts's own "Local runtime-only
 * fields" comment for the fields that do NOT appear here.
 */
export interface PatinfoHistoryAdminResponseDto {
  RecId: string;
  RecDate: string;
  RecUTM: string;
  ID: string;
  RecPatient: string;
  RecAssessment: string;
  SurveybList: string;
  RecStatus: string;
  RecClient: string;
  RecPractice: string;
  RecSite: string;
  RecStation: string;
  RecSN: string;
  RecYear: string;
  RecQtr: string;
  RecMon: string;
  RecWeek: string;
  RecDay: string;
  Duration: string;
  RecProvider: string;
  RecLang: string;
  ResponseData: Array<{ Name: string; Value: string }>;
}

/**
 * A profile's assessment history — previously just a RecId reference requiring a second
 * backend trip to resolve the full AdminResponseDto for report generation; now the full
 * response is embedded directly (frontloaded per-profile on search).
 */
export interface PatinfoHistoryDto {
  PracDName: string;
  Admin: PatinfoHistoryAdminResponseDto;
}

/**
 * Converts the wire (PascalCase) embedded AdminResponseDto into the local camelCase shape
 * everything else in the app (AdminResponseStorage, ReportView, reportRenderer) expects.
 * Reads defensively (camelCase ?? PascalCase) matching the established normalization
 * convention (AdminAssessmentStorage's PascalCase->camelCase content normalization,
 * AdminView.tsx's submit-response handling). Fields not present in the C# DTO (see
 * AdminResponseDto.ts's "Local runtime-only fields") default to '' — they're populated
 * locally elsewhere, never carried on the wire.
 */
export function normalizeHistoryAdminResponse(wire: PatinfoHistoryAdminResponseDto | null | undefined): AdminResponseDto {
  const w = (wire ?? {}) as Record<string, unknown>;
  const read = (camel: string, pascal: string): string => (w[camel] as string) ?? (w[pascal] as string) ?? '';
  const responseData = ((w.responseData as unknown[]) ?? (w.ResponseData as unknown[]) ?? []).map((item) => {
    const r = item as Record<string, unknown>;
    return {
      name: (r.name as string) ?? (r.Name as string) ?? '',
      value: (r.value as string) ?? (r.Value as string) ?? '',
    };
  });

  return {
    recId: read('recId', 'RecId'),
    recDate: read('recDate', 'RecDate'),
    recUTM: read('recUTM', 'RecUTM'),
    id: read('id', 'ID'),
    recPatient: read('recPatient', 'RecPatient'),
    recAssessment: read('recAssessment', 'RecAssessment'),
    surveybList: read('surveybList', 'SurveybList'),
    recStatus: read('recStatus', 'RecStatus'),
    recClient: read('recClient', 'RecClient'),
    recPractice: read('recPractice', 'RecPractice'),
    recSite: read('recSite', 'RecSite'),
    recStation: read('recStation', 'RecStation'),
    recSN: read('recSN', 'RecSN'),
    recYear: read('recYear', 'RecYear'),
    recQtr: read('recQtr', 'RecQtr'),
    recMon: read('recMon', 'RecMon'),
    recWeek: read('recWeek', 'RecWeek'),
    recDay: read('recDay', 'RecDay'),
    duration: read('duration', 'Duration'),
    recProvider: read('recProvider', 'RecProvider'),
    recLang: read('recLang', 'RecLang'),
    responseData,
    // Local-only fields — never present on the wire.
    recAlias: '',
    recSurvey: '',
    seq_Num: '',
    recstate: '',
    recFirst: '',
    recDepartment: '',
    recSpecialty: '',
    recGroup: '',
    recLTM: '',
    id_RC: '',
    recPeriod: '',
    surveyAliasList: '',
  };
}

export interface PatinfoScheduleDto {
  regid: string;
  schedtype: string;
  startdate: string;
  schednext: string;
  terminate: string;
  episode: string;
  /** '0' = disabled, '1' = enabled — explicit so unchecking a touchpoint schedule is a real,
   * detectable change rather than just the item's absence from the array. */
  status: string;
}

export interface PatinfoConnectionsDto {
  id: string;
  type: string;
  group: string;
  mpatconn: string;
  srcpatient: string;
  srcpractice: string;
  srcpracticedname: string;
  destpatient: string;
  destpractice: string;
  destpracticedname: string;
  refcode: string;
  status: string;
}

export interface PatinfoFlagsDto {
  id: string;
  dname: string;
  type: string;
  group: string;
  mpatconn: string;
  srcpatient: string;
  srcpractice: string;
  srcpracticedname: string;
  destpatient: string;
  destpractice: string;
  destpracticedname: string;
  recid: string;
  refcode: string;
  severity: string;
  disposition: string;
  status: string;
}

export interface PatinfoEpisodesDto {
  id: string;
  dname: string;
  initdate: string;
  outcomeinst: string;
  currentphase: string;
  disposition: string;
  closedate: string;
}

export interface PatinfoMessagesDto {
  name: string;
  value: string;
}

export interface PatinfoProfileDto {
  patcode: string;
  patientid: string;
  phi: PatinfoPhiDto[];
  launches: LaunchSecureDto[];
  history: PatinfoHistoryDto[];
  schedule: PatinfoScheduleDto[];
  connections: PatinfoConnectionsDto[];
  flags: PatinfoFlagsDto[];
  episodes: PatinfoEpisodesDto[];
  messages: PatinfoMessagesDto[];
  modified: string;
  status: string;
}

export interface PatinfoSearchResponseDto {
  type: PatinfoSearchType;
  blocknum: string;
  blocksize: string;
  results: PatinfoSearchResultDto[];
  profiles: PatinfoProfileDto[];
}

// ── Profile new (create) ─────────────────────────────────────────────────────

/**
 * Payload for provider_profile_new.
 * patid and dob may be blank; backend can generate patient id when patid is blank.
 */
export interface PatinfoNewRequestDto {
  patid: string;
  fname: string;
  lname: string;
  dob: string;
}

export interface PatinfoNewResponseDto {
  result: 'new' | 'exists' | string;
  profile: PatinfoProfileDto;
}

// ── Profile put (create/update) ───────────────────────────────────────────────

/**
 * Payload for provider_profile_put.
 * Only populate fields that have changed; omit unchanged arrays/sub-objects.
 */
export interface PatinfoPutRequestDto {
  patcode?: string;
  patientid?: string;
  phi?: PatinfoPhiDto[];
  launches?: LaunchSecureDto[];
  history?: PatinfoHistoryDto[];
  schedule?: PatinfoScheduleDto[];
  connections?: PatinfoConnectionsDto[];
  flags?: PatinfoFlagsDto[];
  episodes?: PatinfoEpisodesDto[];
  messages?: PatinfoMessagesDto[];
  modified?: string;
  status?: string;
}

export interface PatinfoPutResponseDto {
  patcode: string;
  patientid: string;
  success: string;
  message: string;
}

// ── PHI field name constants (canonical names used in PatinfoPhiDto.name) ────

export const PHI_FIELD = {
  FNAME: 'fname',
  LNAME: 'lname',
  DOB: 'dob',
  PATID: 'patid',
  PATIENTID: 'patientid',
  GENDER: 'gender',
  EMAIL: 'email',
  PHONE: 'phone',
  ADDRESS: 'address',
  /** Selected "Send Assessment By" radio option key (notifytype). */
  HOWCONTACT: 'howcontact',
  /** Copy/Paste Link send option enabled — stored as '1' / '0'. */
  COPYPASTE: 'copypaste',
  /** Selected Local office SRA member id for the "Local" send option. */
  LOCALSRA: 'localsra',
} as const;

export type PhiFieldName = (typeof PHI_FIELD)[keyof typeof PHI_FIELD];

/** Convenience: extract a PHI value by field name from a phi array */
export function getPhiValue(phi: PatinfoPhiDto[], name: string): string {
  const exact = phi.find(p => p.name === name)?.value;
  if (typeof exact === 'string') {
    return exact;
  }

  const nameLower = name.toLowerCase();
  return phi.find(p => p.name?.toLowerCase() === nameLower)?.value ?? '';
}

/** Convenience: set or replace a PHI value in a phi array (immutable) */
export function setPhiValue(phi: PatinfoPhiDto[], name: string, value: string): PatinfoPhiDto[] {
  const nameLower = name.toLowerCase();
  const exists = phi.some(p => p.name === name || p.name?.toLowerCase() === nameLower);
  if (exists) {
    return phi.map(p => (p.name === name || p.name?.toLowerCase() === nameLower) ? { name, value } : p);
  }
  return [...phi, { name, value }];
}
